/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getCapabilities,
  getHealth,
  getMe,
  getTicketMetadata,
  type KioskProfile,
  logout,
  searchDirectory,
  type TicketAttachment,
  uploadDraftAttachment,
} from "@/lib/api/generated";
import { meRetryDelay, shouldRetryMe } from "@/lib/session-error";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Never retried on 401/403 — ProtectedRoute redirects to /login. A 5xx
    // (SERVICE_STARTING during a restart) is retried briefly instead, so a
    // server hiccup does not sign everyone out; a dropped connection was
    // already retried by the API client (`@/lib/network-retry`, 12 Oct).
    retry: shouldRetryMe,
    retryDelay: meRetryDelay,
    // The one query re-checked when the tab regains focus (the app default is
    // off). Someone coming back to a tab left open overnight is sent to sign
    // in *before* they type, with this page as the return path, instead of
    // losing a message to a 401 on Send. Only once `staleTime` has passed, and
    // a failure that is not a 401/403 keeps the page (see ProtectedRoute).
    refetchOnWindowFocus: true,
  });
}

/**
 * The shared device this session runs on, or null for a personal session.
 *
 * Null until the profile has loaded too, so nothing kiosk-specific flashes up
 * for a normal user (1 Oct).
 */
export function useKiosk(): KioskProfile | null {
  const { data: me } = useMe();
  return me?.kiosk ?? null;
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Deployment capabilities ─────────────────────────────────────────────

/**
 * What this deployment is actually wired to — real integrations or fixtures.
 *
 * The field that matters to a user is `demo_knowledge_corpus`. When it is true
 * the answers on `/ask` come from a seeded
 * in-memory corpus: invented guidance under invented ClickLearn URLs, dressed
 * in exactly the same cards and "Updated" dates as the real thing. Every
 * surface that shows those answers has to say so, which is why this is fetched
 * once and cached hard rather than being derived per page.
 *
 * `staleTime: Infinity` because the answer is fixed for the life of the server
 * process — it is decided when the retriever is built, not per request.
 */
export function useCapabilities() {
  return useQuery({
    queryKey: ["capabilities"],
    queryFn: async () => {
      const { data } = await getCapabilities();
      return data;
    },
    staleTime: Infinity,
    // A signed-out user has no answers on screen to mislabel, and
    // ProtectedRoute is already sending them to /login.
    retry: false,
  });
}

// ─── Directory ───────────────────────────────────────────────────────────

export const directoryKeys = {
  all: ["directory"] as const,
  employees: (q: string, limit: number) => [...directoryKeys.all, "employees", q, limit] as const,
} as const;

/**
 * Shortest query worth sending.
 *
 * One letter matches most of a 1,600-person directory, so the server would do
 * real work to hand back a list nobody can use. Two is the point at which the
 * result set starts to mean something.
 */
export const DIRECTORY_MIN_QUERY_LENGTH = 2;

export interface UseDirectoryEmployeesOptions {
  /** Upper bound on the number of matches asked for. */
  limit?: number;
  /** Set false to hold the search off entirely (a disabled field). */
  enabled?: boolean;
}

/**
 * Employee directory type-ahead.
 *
 * Keyed on the trimmed query so every distinct search is cached separately and
 * backspacing re-reads rather than re-fetches. `placeholderData: keepPreviousData`
 * is what stops the list emptying and re-filling between keystrokes — without
 * it the popup flashes its empty state on every debounce boundary, which reads
 * as "no such person" for a few hundred milliseconds.
 *
 * A query shorter than {@link DIRECTORY_MIN_QUERY_LENGTH} is not sent at all;
 * the hook stays idle and the caller shows a "keep typing" prompt.
 */
export function useDirectoryEmployees(query: string, options: UseDirectoryEmployeesOptions = {}) {
  const { limit = 20, enabled = true } = options;
  const q = query.trim();

  return useQuery({
    queryKey: directoryKeys.employees(q, limit),
    queryFn: async () => {
      const { data } = await searchDirectory({ query: { q, limit } });
      return data;
    },
    enabled: enabled && q.length >= DIRECTORY_MIN_QUERY_LENGTH,
    placeholderData: keepPreviousData,
    // The directory changes when HR changes, not when the user types.
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Draft attachments ──────────────────────────────────────────────────

/**
 * Upload one file before its ticket exists.
 *
 * This is the single upload path shared by every create surface — the portal
 * `/tickets/new` form, the ticket detail page, and both AskIT flows. It posts
 * multipart form data to `POST /tickets/drafts/attachments` through the
 * generated SDK (which carries the `formDataBodySerializer`, so the caller
 * hands over a plain `File`) and resolves with the {@link TicketAttachment}
 * whose `attachment_id` the create call later claims.
 *
 * The server owns every safety check: it enforces the size cap
 * (`ATTACHMENT_TOO_LARGE`), the content-type allowlist
 * (`ATTACHMENT_TYPE_NOT_ALLOWED`) and the malware scan (`ATTACHMENT_NOT_CLEAN`),
 * so a rejected file rejects this mutation with the usual `{ status, body }`
 * envelope and the caller surfaces the translated code. No retry: a rejected
 * upload is rejected for a reason the bytes will not change on a replay.
 */
/** The per-file upload cap to assume until metadata loads: the backend default (10 MiB). */
export const DEFAULT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * The server's per-file upload cap, so an oversized file is refused before it
 * is sent. Shares the tickets feature's metadata cache key, so it costs no
 * extra request where ticket metadata is already loaded.
 */
export function useAttachmentMaxBytes(): number {
  const { data } = useQuery({
    queryKey: ["tickets", "metadata"],
    queryFn: async () => (await getTicketMetadata({ throwOnError: true })).data,
    staleTime: 10 * 60_000,
  });
  return data?.max_attachment_bytes ?? DEFAULT_ATTACHMENT_MAX_BYTES;
}

export interface UploadDraftAttachmentVariables {
  file: File;
  /** Stop waiting, e.g. `AbortSignal.timeout(…)`, so a stalled upload fails. */
  signal?: AbortSignal;
}

export function useUploadDraftAttachment() {
  return useMutation<TicketAttachment, unknown, UploadDraftAttachmentVariables>({
    mutationFn: async ({ file, signal }: UploadDraftAttachmentVariables) => {
      const { data } = await uploadDraftAttachment({
        body: { file },
        throwOnError: true,
        ...(signal === undefined ? {} : { signal }),
      });
      return data;
    },
    retry: false,
  });
}
